import { differenceInCalendarMonths } from 'date-fns'
import { Address, AddressSchema, emptyAddress } from 'shared/components/Address/AddressFields'
import { Address as AddressWithid } from 'shared/models/address'
import { DriverPayload, User } from 'shared/models/user'
import { mixed, object, string } from 'yup'

export const MIN_DOB_IN_YEARS = 23
export const SSN_ENCRYPTED = '•••••••••'

export const CDLSchema = object().shape({
  firstName: string().required(),
  lastName: string().required(),
  dob: mixed().test(function (val) {
    if (!val) {
      return this.createError({ message: 'This field is required.', path: 'dob' })
    }
    if (differenceInCalendarMonths(new Date(), new Date(val)) < MIN_DOB_IN_YEARS * 12) {
      return this.createError({
        message: `You must be at least ${MIN_DOB_IN_YEARS} years old.`,
        path: 'dob',
      })
    }
    return true
  }),
  ssn: mixed().test(function (val) {
    if (!val) {
      return this.createError({ message: 'This field is required.', path: 'ssn' })
    }
    if (String(val).replace(/(\s|_)/g, '').length !== 9) {
      return this.createError({ message: 'Please add 9 digit SSN number.', path: 'ssn' })
    }
    return true
  }),
  address: AddressSchema(),
  licenseNumber: string().required('This field is required.'),
  licenseState: string().required('This field is required.'),
  licenseIssued: mixed().test(function (val) {
    if (!val) {
      return this.createError({ message: 'This field is required', path: 'licenseIssued' })
    }
    if (new Date(val) > new Date()) {
      return this.createError({
        message: 'Please select date in the past.',
        path: 'licenseIssued',
      })
    }
    return true
  }),
  licenseExpiration: mixed().test(function (val) {
    if (!val) {
      return this.createError({ message: 'This field is required', path: 'licenseExpiration' })
    }
    if (new Date(val) <= new Date()) {
      return this.createError({
        message: 'Please select date in the future.',
        path: 'licenseExpiration',
      })
    }
    return true
  }),
})

export type CDLForm = {
  ssn?: string
  firstName?: string
  middleName?: string
  lastName?: string
  dob?: string
  licenseNumber?: string
  licenseState?: string
  licenseIssued?: string
  licenseExpiration?: string
  address: Address
}

// use either parsedCDL or user to populate the form don't mix and match
export const getInitialForm = (parsedCDL: CDLForm | null, user: User | null): CDLForm => {
  // if parsedCDL exists, use it to populate the form
  if (parsedCDL) {
    return {
      ssn: '',
      firstName: parsedCDL?.firstName || user?.firstName || '',
      middleName: parsedCDL?.middleName || user?.middleName || '',
      lastName: parsedCDL?.lastName || user?.lastName || '',
      dob: parsedCDL?.dob || user?.driver?.dob || '',
      licenseNumber:
        parsedCDL?.licenseNumber || user?.driver?.licenseDocument?.metadata.DRIVER_LICENSE_NUMBER || '',
      licenseState:
        parsedCDL?.licenseState || user?.driver?.licenseDocument?.metadata.DRIVER_LICENSE_STATE || '',
      licenseIssued:
        parsedCDL?.licenseIssued || user?.driver?.licenseDocument?.metadata.DRIVER_LICENSE_ISSUE_DATE || '',
      licenseExpiration: parsedCDL?.licenseExpiration || user?.driver?.licenseDocument?.dateExpired || '',
      address: parsedCDL?.address || user?.shippingAddress || emptyAddress,
    }
  }

  // if parsedCDL does not exist, use user to populate the form
  return {
    ssn: '',
    firstName: user?.firstName || '',
    middleName: user?.middleName || '',
    lastName: user?.lastName || '',
    dob: user?.driver?.dob || '',
    licenseNumber: user?.driver?.licenseDocument?.metadata.DRIVER_LICENSE_NUMBER || '',
    licenseState: user?.driver?.licenseDocument?.metadata.DRIVER_LICENSE_STATE || '',
    licenseIssued: user?.driver?.licenseDocument?.metadata.DRIVER_LICENSE_ISSUE_DATE || '',
    licenseExpiration: user?.driver?.licenseDocument?.dateExpired || '',
    address: user?.shippingAddress || emptyAddress,
  }
}

export const getCreateCDLApplicationDriverBody = (form: Required<CDLForm>): DriverPayload => {
  return {
    dob: form.dob,
    ssn_encrypted: form.ssn === SSN_ENCRYPTED ? undefined : form.ssn,
    license_number: form.licenseNumber,
    license_state: form.licenseState,
    license_issued: form.licenseIssued,
    license_expiration: form.licenseExpiration,
  }
}

export const getCreateAddressBody = (applicationForm: Required<CDLForm>): AddressWithid => {
  return {
    id: '',
    line1: applicationForm.address.line1,
    line2: applicationForm.address.line2,
    city: applicationForm.address.city,
    state: applicationForm.address.state,
    country: '',
    zip: applicationForm.address.zip,
  }
}
